import { flagDescriptor, hasFlag, type PermissionFlag } from '@ofmos/shared/roles';
import type { ReactNode } from 'react';
import { EmptyState, Spinner } from '../../ui';
import { useSession } from './useSession';

/**
 * A route behind one PERMISSION FLAG, beside `RequireAuth` and `RequireAdmin`.
 *
 * `RequireAdmin` reads the LEGACY `UserRole`, which `shared/src/auth.ts` says is derived
 * and read by nothing for authorization. That was fine while every gated screen was
 * "admin or not"; the CSV screens are not. `IMPORT_CSV` and `EXPORT_CSV` are seeded to
 * `Operator` and `Owner` (D33, Arif 2026-08-04), and an instance can grant either onward
 * to a role that is not an administrator - so a guard keyed on `role === 'admin'` would
 * either lock out somebody who holds the flag or admit somebody who does not.
 *
 * THE BROWSER GUARD IS A COURTESY, NEVER THE WALL, exactly as `RequireAdmin` says: every
 * route behind it asks `requireFlag` on the server, twice for a download (D33). This
 * exists so a person is TOLD rather than shown a screen whose every button will refuse
 * them - and the sentence names the permission, because "ask an admin" is only actionable
 * if the reader can say what to ask for.
 */
export function RequireFlag({
  flag,
  children,
}: {
  readonly flag: PermissionFlag;
  readonly children: ReactNode;
}) {
  const session = useSession();

  if (session.isPending) {
    return (
      <div className="flex min-h-64 items-center justify-center py-12" aria-busy="true">
        <Spinner label="Checking your access" />
      </div>
    );
  }

  const user = session.data;
  if (user == null || !hasFlag(user.permissions, flag)) {
    return (
      <EmptyState
        icon="blocked"
        title="You do not have this permission"
        description={`This screen needs the "${flagDescriptor(flag).label}" permission, which your access roles do not include. An administrator can grant it on the access role you hold.`}
      />
    );
  }

  return children;
}
